import { CloseCircleOutlined } from '@ant-design/icons';
import { Flex } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  ITAdminNotification,
  PlantEngineerNotification,
  PlantWorkshopManagerNotification,
  PurchaseOfficerNotification,
  RequiredNotification,
  SimAddonNotification,
} from '@Components/index';
import {
  useFormApproveMutation,
  useItRequisitionAprroveMutation,
  useRequisitionAprroveMutation,
} from '@Store/api/approvalsListApiSlice';
import { FormApproveRejectRequest } from '@Types/api/form';
import { ApprovalActions } from '@Enum/index';
import { deciderOfApprovePathName } from '@Utils/deciderOfServicePathName';
import { useAppDispatch, useAppSelector } from '@Hooks/index';
import { stateOpenRejectModal } from '@Store/selectors/approvalsListSelector';
import { approvalsListActions } from '@Store/slices/approvalsListSlice';
import { NotificationContext } from '@Context/NotificationContext';
import { ItRequisitionTypeCodes } from '@Enum/api/ItRequisitionTypeCodes.enum';
import { ItRequisitionStatusCodes } from '@Enum/api/ItRequisitionStatusCodes';
import { RequisitionStatuses } from '@Features/MaterialRequest/enum/RequisitionStatuses.enum';

import {
  useGetExistingPackageMutation,
  useGetITRequestFormQuery,
} from '@Features/EmployeeSelfService/requests/ITRequisition/store/api/itRequisitionApiSlice';
import {
  useGetRequisitionCommodityClassQuery,
  useGetRequisitionQuery,
} from '@Features/MaterialRequest/requests/MaterialRequisition/store/api/MaterialRequestApiSlice';

import { useFormik } from 'formik';
import {
  initITAdmin,
  initPlantEngineer,
  initPlantWorkshopManager,
  initPurchaseOfficer,
  initSimAddonFormikValues,
} from './initFormikValues';

interface RejectTaskProps {
  taskId: string;
  requestNo: string | undefined;
  requestId: number;
  requestTypeId: string;
  statusId: number | undefined;
  onReject?: () => void;
}

interface ItRequisitionProcessProps {
  body: FormApproveRejectRequest;
}

interface HRMProcessProps {
  body: FormApproveRejectRequest;
}

const RejectTask: React.FC<RejectTaskProps> = ({
  taskId,
  requestNo,
  requestId,
  requestTypeId,
  statusId,
  onReject,
}) => {
  const [rejectCommentValue, setRejectCommentValue] = useState('');
  const [existingPackageValue, setExistingPackageValue] = useState('');
  const { taskRejectedNotification, taskErrorNotification } =
    useContext(NotificationContext);
  const stateOpenModal = useAppSelector(stateOpenRejectModal);
  const dispatch = useAppDispatch();
  const [postApproveRejectForm] = useFormApproveMutation();
  const [requisitionApproveRejectForm] = useRequisitionAprroveMutation();
  const [itReqApproveRejectForm] = useItRequisitionAprroveMutation();
  const [getExistingPackage] = useGetExistingPackageMutation();

  const isMR = requestTypeId === 'Requisition' || requestTypeId === 'MRRequest';
  const { data: mrRequestData } = useGetRequisitionQuery(String(requestId), {
    skip: requestTypeId !== 'Requisition',
  });

  const { data: itRequestData } = useGetITRequestFormQuery(String(requestId), {
    skip: requestTypeId !== 'IT Requisition',
  });

  const { data: isShowITAdmin } = useGetRequisitionCommodityClassQuery(
    mrRequestData?.requisitionId || 0,
    {
      skip:
        mrRequestData?.statusId !==
        RequisitionStatuses.AWAITING_APPROVAL_OF_WAREHOUSE_MANAGER,
    }
  );

  useEffect(() => {
    if (itRequestData?.requestId) {
      getExistingPackage(itRequestData.requestId)
        .unwrap()
        .then((response) => setExistingPackageValue(response?.packages || ''));
    }
  }, [getExistingPackage, itRequestData?.requestId]);

  const isSimAddonRequest = useMemo(() => {
    if (
      itRequestData?.reqItemId === ItRequisitionTypeCodes.SIM_ADDON_REQUEST &&
      itRequestData?.statusId === ItRequisitionStatusCodes.AWAITING_APPROVAL &&
      !itRequestData?.recommendPackage
    ) {
      return true;
    }

    return false;
  }, [
    itRequestData?.recommendPackage,
    itRequestData?.reqItemId,
    itRequestData?.statusId,
  ]);

  const isPurchaseOfficerRequest = useMemo(() => {
    if (
      mrRequestData?.statusId ===
      RequisitionStatuses.AWAITING_APPROVAL_OF_PROCUREMENT_MANAGER
    ) {
      return true;
    }
    return false;
  }, [mrRequestData]);

  const isPlantEngineerRequest = useMemo(() => {
    if (
      mrRequestData?.statusId ===
      RequisitionStatuses.AWAITING_APPROVAL_OF_SENIOR_STORE_KEEPER
    ) {
      return true;
    }

    return false;
  }, [mrRequestData?.statusId]);

  const isPlantWorkshopManagerRequest = useMemo(() => {
    if (
      mrRequestData?.statusId ===
      RequisitionStatuses.AWAITING_APPROVAL_OF_PLANT_ENGINEER
    ) {
      return true;
    }

    return false;
  }, [mrRequestData?.statusId]);

  const isITAdminRequest = useMemo(() => {
    if (
      mrRequestData?.statusId ===
        RequisitionStatuses.AWAITING_APPROVAL_OF_WAREHOUSE_MANAGER &&
      isShowITAdmin
    ) {
      return true;
    }

    return false;
  }, [mrRequestData?.statusId, isShowITAdmin]);

  const formikSimAddon = useFormik({
    initialValues: initSimAddonFormikValues,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikPurchaseOfficer = useFormik({
    initialValues: initPurchaseOfficer,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikPlantEngineer = useFormik({
    initialValues: initPlantEngineer,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikPlantWorkshopManager = useFormik({
    initialValues: initPlantWorkshopManager,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikITAdmin = useFormik({
    initialValues: initITAdmin,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const handleRejectPurchaseOfficerModal = useCallback((): void => {
    const body = {
      taskId: String(taskId),
      decision: ApprovalActions.REJECT,
      selectedApproverUserId: 0,
      comment: '',
    };

    requisitionApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskRejectedNotification();
        onReject?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  }, [
    taskId,
    requisitionApproveRejectForm,
    taskRejectedNotification,
    onReject,
    taskErrorNotification,
  ]);

  const handleRejectPlantEngineerModal = useCallback((): void => {
    const { comment, plantEngineer } = formikPlantEngineer.values;

    const body = {
      taskId: String(taskId),
      decision: ApprovalActions.REJECT,
      selectedApproverUserId: Number(plantEngineer),
      comment,
    };

    requisitionApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskRejectedNotification();
        onReject?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  }, [
    taskId,
    requisitionApproveRejectForm,
    taskRejectedNotification,
    onReject,
    taskErrorNotification,
    formikPlantEngineer.values,
  ]);

  const handleRejectPlantWorkshopManagerModal = useCallback((): void => {
    const { comment, plantWorkshopManager } = formikPlantWorkshopManager.values;

    const body = {
      taskId: String(taskId),
      decision: ApprovalActions.REJECT,
      selectedApproverUserId: Number(plantWorkshopManager),
      comment,
    };

    requisitionApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskRejectedNotification();
        onReject?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  }, [
    taskId,
    requisitionApproveRejectForm,
    taskRejectedNotification,
    onReject,
    taskErrorNotification,
    formikPlantWorkshopManager.values,
  ]);

  const handleRejectITAdminModal = useCallback((): void => {
    const { comment, itAdmin } = formikITAdmin.values;

    const body = {
      taskId: String(taskId),
      decision: ApprovalActions.REJECT,
      selectedApproverUserId: Number(itAdmin),
      comment,
    };

    requisitionApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskRejectedNotification();
        onReject?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  }, [
    taskId,
    requisitionApproveRejectForm,
    taskRejectedNotification,
    onReject,
    taskErrorNotification,
    formikITAdmin.values,
  ]);

  const handleRejectSimAddonModal = useCallback((): void => {
    const { recommendPackage } = formikSimAddon.values;
    const body = {
      taskId: String(taskId),
      command: ApprovalActions.REJECT,
      comment: '',
      existingPackage: existingPackageValue,
      recommendPackage,
    };

    itReqApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskRejectedNotification();
        onReject?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  }, [taskId, existingPackageValue, formikSimAddon.values]);

  const showModal = (event: React.FormEvent): void => {
    const target = event.target as HTMLInputElement;
    const buttonId = (event.target as HTMLButtonElement)?.id;
    const targetText = target?.textContent;

    if (buttonId !== 'cancel' && targetText !== 'No, Thanks') {
      dispatch(approvalsListActions.setOpenModalReject(true));
    }
  };

  const servicePathName = useMemo(
    () => deciderOfApprovePathName(requestTypeId),
    [requestTypeId]
  );

  const onItRequisitionProcess = ({
    body,
  }: ItRequisitionProcessProps): void => {
    itReqApproveRejectForm({
      ...body,
    })
      .unwrap()
      .then(() => {
        taskRejectedNotification();
        onReject?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  };

  const onRequisitionProcess = (): void => {
    requisitionApproveRejectForm({
      comment: rejectCommentValue,
      decision: ApprovalActions.REJECT,
      selectedApproverUserId: 0,
      taskId: String(taskId),
    })
      .unwrap()
      .then(() => {
        taskRejectedNotification();
        onReject?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  };

  const onHRMProcess = ({ body }: HRMProcessProps): void => {
    postApproveRejectForm({
      servicePathName,
      body,
    })
      .unwrap()
      .then(() => {
        taskRejectedNotification();
        onReject?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  };

  const handleOk = useCallback((): void => {
    const body: FormApproveRejectRequest = {
      taskId: String(taskId),
      command: ApprovalActions.REJECT,
      comment: rejectCommentValue,
    };

    if (requestTypeId === 'IT Requisition') {
      onItRequisitionProcess({
        body,
      });
    }

    if (isMR) {
      onRequisitionProcess();
    }

    if (!isMR && requestTypeId !== 'IT Requisition') {
      onHRMProcess({ body });
    }

    dispatch(approvalsListActions.setOpenModalReject(false));
  }, [
    itRequestData,
    mrRequestData,
    statusId,
    taskId,
    existingPackageValue,
    requestTypeId,
    rejectCommentValue,
  ]);

  const handleCancel = (): void => {
    dispatch(approvalsListActions.setOpenModalReject(false));
  };

  const message = useMemo(
    () => `It seems like you're about to reject
  request ${isMR ? requestNo : requestId}. Are you sure you want to proceed with this action?`,
    [requestId, requestNo, isMR]
  );

  return (
    <Flex className="trojan-action-space" gap={15} onClick={showModal}>
      <CloseCircleOutlined /> <span>Reject</span>
      {isSimAddonRequest && (
        <SimAddonNotification
          title="Reject Request"
          isModalOpen={stateOpenModal}
          textSuccessBtn="Yes, Reject"
          handleCancel={handleCancel}
          handleSuccess={handleRejectSimAddonModal}
          formik={formikSimAddon}
          existingPackage={existingPackageValue}
        />
      )}
      {isPurchaseOfficerRequest && (
        <PurchaseOfficerNotification
          title="Reject Request"
          isModalOpen={stateOpenModal}
          message={message}
          textSuccessBtn="Yes, Reject"
          handleCancel={handleCancel}
          handleSuccess={handleRejectPurchaseOfficerModal}
          formik={formikPurchaseOfficer}
        />
      )}
      {isPlantEngineerRequest && (
        <PlantEngineerNotification
          title="Reject Request"
          message={message}
          isModalOpen={stateOpenModal}
          textSuccessBtn="Yes, Reject"
          handleCancel={handleCancel}
          handleSuccess={handleRejectPlantEngineerModal}
          formik={formikPlantEngineer}
        />
      )}
      {isPlantWorkshopManagerRequest && (
        <PlantWorkshopManagerNotification
          title="Reject Request"
          message={message}
          isModalOpen={stateOpenModal}
          textSuccessBtn="Yes, Reject"
          handleCancel={handleCancel}
          handleSuccess={handleRejectPlantWorkshopManagerModal}
          formik={formikPlantWorkshopManager}
        />
      )}
      {isITAdminRequest && mrRequestData?.compId && (
        <ITAdminNotification
          title="Reject Request"
          message={message}
          isModalOpen={stateOpenModal}
          textSuccessBtn="Yes, Reject"
          handleCancel={handleCancel}
          handleSuccess={handleRejectITAdminModal}
          formik={formikITAdmin}
          compId={mrRequestData?.compId}
        />
      )}
      {!isSimAddonRequest &&
        !isPurchaseOfficerRequest &&
        !isPlantEngineerRequest &&
        !isPlantWorkshopManagerRequest &&
        !isITAdminRequest && (
          <RequiredNotification
            title="Reject Request"
            message={message}
            isModalOpen={stateOpenModal}
            textSuccessBtn="Yes, Reject"
            handleCancel={handleCancel}
            handleSuccess={handleOk}
            handleCommentChange={setRejectCommentValue}
          />
        )}
    </Flex>
  );
};

export default RejectTask;
